import { parseMoneyValue } from "@/utils/parse-money";
import {
  PoContractUpdateData,
  PoInitialContractData,
} from "../use-po-contracts-update-flow.types";

export const getAlteredUpdateFields = (
  poContractUpdateData: PoContractUpdateData | null,
  poInitialContractData: PoInitialContractData | null,
): {
  alteredFields:
    | (Partial<PoContractUpdateData> & {
        setToIndeterminate: boolean;
        takePercentage: number;
        partnerPrice: number;
      })
    | Record<string, never>;
} => {
  if (!poContractUpdateData || !poInitialContractData) {
    return {
      alteredFields: {},
    };
  }

  type FieldType = "string" | "numeric" | "boolean" | "monetary";

  const fieldTypes: Record<string, FieldType> = {
    startDate: "string",
    endDate: "string",
    takeRatePercentage: "numeric",
    partnerValue: "monetary",
    monthlyCredits: "numeric",
    members: "numeric",
    hasUndeterminedTime: "boolean",
    packageId: "numeric",
    dealId: "numeric",
    roomResponsibleUserId: "numeric",
    contractResponsibleUserId: "numeric",
    acceptanceTerm: "numeric",
    knowledgeTerm: "numeric",
    expectedDurationMonths: "numeric",
    notes: "string",
    noticeInDaysClient: "numeric",
    noticeInDaysPartner: "numeric",
    renewalIndicator: "string",
    repass: "numeric",
    poType: "string",
  };

  const fieldNameMapping: Record<string, string> = {
    hasUndeterminedTime: "setToIndeterminate",
    takeRatePercentage: "takePercentage",
    partnerValue: "partnerPrice",
  };

  const processField = (fieldKey: string): Record<string, unknown> => {
    const updateValue =
      poContractUpdateData?.[fieldKey as keyof PoContractUpdateData];

    const initialValue =
      poInitialContractData?.[fieldKey as keyof PoInitialContractData];

    const outputKey = fieldNameMapping[fieldKey] || fieldKey;
    const fieldType = fieldTypes[fieldKey];

    switch (fieldType) {
      case "string":
      case "boolean":
        return updateValue !== initialValue ? { [outputKey]: updateValue } : {};

      case "numeric": {
        const numericUpdate = Number(updateValue);
        const numericInitial = Number(initialValue);

        if (!isNaN(numericUpdate) && numericUpdate !== numericInitial) {
          if (fieldKey === "dealId") {
            return { [outputKey]: String(numericUpdate) };
          } else {
            return { [outputKey]: numericUpdate };
          }
        }
        return {};
      }

      case "monetary": {
        const parsedUpdateValue = updateValue
          ? parseMoneyValue(updateValue as string)
          : 0;
        const numericInitialValue = Number(initialValue) || 0;

        return parsedUpdateValue !== numericInitialValue
          ? { [outputKey]: parsedUpdateValue }
          : {};
      }

      default:
        return {};
    }
  };

  const alteredFields = Object.keys(fieldTypes).reduce((acc, fieldKey) => {
    const result = processField(fieldKey);
    return Object.keys(result).length > 0 ? Object.assign(acc, result) : acc;
  }, {});

  return {
    alteredFields,
  };
};
